import React from 'react';
import {motion} from 'framer-motion';
import {useInView} from 'react-intersection-observer';
import {MessageIcon,DesignIcon,ProductionIcon,DeliveryIcon,PhoneIcon,MailIcon} from './icons/ModernIcons';

const Process=()=> {
  const {ref,inView}=useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const steps=[
    {
      icon: MessageIcon,
      title: 'Beratung',
      description: 'Kontaktieren Sie uns für eine individuelle Beratung zu Ihrem Projekt',
      color: 'blue'
    },
    {
      icon: DesignIcon,
      title: 'Design',
      description: 'Wir besprechen Ihr Design und wählen das optimale Druckverfahren',
      color: 'purple'
    },
    {
      icon: ProductionIcon,
      title: 'Produktion',
      description: 'Professionelle Umsetzung mit modernster Technik und Qualitätskontrolle',
      color: 'green'
    },
    {
      icon: DeliveryIcon,
      title: 'Lieferung',
      description: 'Schnelle Lieferung oder Abholung direkt in Pleinfeld',
      color: 'orange'
    }
  ];

  return (
    <section className="py-20 relative overflow-hidden bg-white">
      {/* Background decorations */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-10 w-64 h-64 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full opacity-20 blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-80 h-80 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full opacity-20 blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          ref={ref}
          initial={{opacity: 0,y: 50}}
          animate={inView ? {opacity: 1,y: 0} : {}}
          transition={{duration: 0.8}}
          className="text-center mb-16"
        >
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            So einfach geht's
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Von der ersten Idee bis zum fertigen Produkt - unser bewährter Prozess garantiert beste Ergebnisse
          </p>
        </motion.div>

        <div className="relative">
          {/* Connection Line */}
          <div className="hidden lg:block absolute top-32 left-1/2 transform -translate-x-1/2 w-3/4 h-1 bg-gradient-to-r from-blue-200 via-purple-200 via-green-200 to-orange-200 rounded-full"></div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step,index)=> (
              <motion.div
                key={step.title}
                initial={{opacity: 0,y: 50}}
                animate={inView ? {opacity: 1,y: 0} : {}}
                transition={{duration: 0.8,delay: index * 0.2}}
                className="relative text-center group"
              >
                {/* Step card */}
                <motion.div
                  whileHover={{y: -10}}
                  className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all border border-gray-100 overflow-hidden"
                >
                  {/* Icon Header */}
                  <div className={`relative h-40 bg-gradient-to-br from-${step.color}-500 to-${step.color}-600 flex items-center justify-center`}>
                    {/* Background Pattern */}
                    <div className="absolute inset-0 opacity-20">
                      <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                        <defs>
                          <pattern id={`pattern-${step.color}-${index}`} width="15" height="15" patternUnits="userSpaceOnUse">
                            <circle cx="7.5" cy="7.5" r="1.5" fill="white" opacity="0.4"/>
                          </pattern>
                        </defs>
                        <rect width="100" height="100" fill={`url(#pattern-${step.color}-${index})`} />
                      </svg>
                    </div>

                    {/* Main Icon */}
                    <motion.div
                      whileHover={{scale: 1.2,rotate: 10}}
                      className="relative z-10 w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center shadow-lg"
                    >
                      <step.icon className="w-10 h-10 text-white" />
                    </motion.div>

                    {/* Step Number */}
                    <div className="absolute top-4 left-4 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg">
                      <span className="text-lg font-bold text-gray-800">{index + 1}</span>
                    </div>

                    {/* Floating elements */}
                    <motion.div
                      animate={{
                        y: [-5,5,-5],
                        rotate: [0,180,360],
                        scale: [1,1.2,1]
                      }}
                      transition={{
                        duration: 6 + index,
                        repeat: Infinity,
                        ease: "easeInOut"
                      }}
                      className="absolute top-4 right-4 w-4 h-4 bg-white/40 rounded-full"
                    />
                    <motion.div
                      animate={{
                        y: [5,-5,5],
                        rotate: [360,180,0],
                        scale: [1.2,1,1.2]
                      }}
                      transition={{
                        duration: 8 - index,
                        repeat: Infinity,
                        ease: "easeInOut"
                      }}
                      className="absolute bottom-4 left-4 w-3 h-3 bg-white/30 rounded-full"
                    />
                    <motion.div
                      animate={{
                        x: [-3,3,-3],
                        y: [3,-3,3],
                        opacity: [0.3,0.8,0.3]
                      }}
                      transition={{
                        duration: 4 + index * 2,
                        repeat: Infinity,
                        ease: "easeInOut"
                      }}
                      className="absolute bottom-4 right-4 w-2 h-2 bg-white/50 rounded-full"
                    />
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
                    <p className="text-gray-600 leading-relaxed text-sm">{step.description}</p>
                    
                    {/* Progress indicator */}
                    <div className="mt-4 flex justify-center">
                      <div className={`w-12 h-1 bg-${step.color}-500 rounded-full`}></div>
                    </div>
                  </div>
                </motion.div>

                {/* Connection arrow for desktop */}
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-32 -right-4 z-20">
                    <motion.div
                      animate={{x: [0,5,0]}}
                      transition={{duration: 2,repeat: Infinity}}
                      className={`w-8 h-8 bg-${step.color}-500 rounded-full flex items-center justify-center shadow-lg`}
                    >
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                    </motion.div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>

        <motion.div
          initial={{opacity: 0,y: 50}}
          animate={inView ? {opacity: 1,y: 0} : {}}
          transition={{duration: 0.8,delay: 0.8}}
          className="text-center mt-16"
        >
          <div className="relative bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 overflow-hidden">
            {/* Background pattern */}
            <div className="absolute inset-0 opacity-10">
              <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                <defs>
                  <pattern id="cta-pattern" width="20" height="20" patternUnits="userSpaceOnUse">
                    <circle cx="10" cy="10" r="2" fill="white" opacity="0.3"/>
                    <circle cx="5" cy="5" r="1" fill="white" opacity="0.2"/>
                    <circle cx="15" cy="15" r="1" fill="white" opacity="0.2"/>
                  </pattern>
                </defs>
                <rect width="100" height="100" fill="url(#cta-pattern)" />
              </svg>
            </div>

            <div className="relative z-10">
              <h3 className="text-2xl font-bold text-white mb-4">
                Bereit für Ihr Projekt?
              </h3>
              <p className="text-blue-100 mb-6">
                Lassen Sie uns gemeinsam Ihre Ideen zum Leben erwecken
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.a
                  href="tel:01755525889"
                  whileHover={{scale: 1.05}}
                  whileTap={{scale: 0.95}}
                  className="inline-flex items-center justify-center space-x-2 bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all"
                >
                  <PhoneIcon className="w-5 h-5" />
                  <span>Jetzt anrufen</span>
                </motion.a>

                <motion.a
                  href="mailto:info@lima-service.de"
                  whileHover={{scale: 1.05}}
                  whileTap={{scale: 0.95}}
                  className="inline-flex items-center justify-center space-x-2 border-2 border-white/30 text-white px-8 py-3 rounded-lg font-semibold hover:bg-white/10 transition-all backdrop-blur-sm"
                >
                  <MailIcon className="w-5 h-5" />
                  <span>E-Mail senden</span>
                </motion.a>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Process;